// What every finance mutation does: never retry, then toast the outcome and refetch all finance data, after a
// refusal too. And how a modal sends one: once per click, never closed mid-flight. Kept pure, free of the store
// and the query client, so the rules can be tested.

export interface MutationEffects {
  toast: (toast: { message: string; type: "success" | "error" }) => void;
  /** Refetches every finance query; resolves once they are back. */
  refetch: () => Promise<void>;
}

/**
 * `retry: 0` overrides the app-wide mutation retry. Finance writes are not idempotent: a mark paid that
 * commits but times out in the browser would post a second payment, and a retried close answers
 * "already closed".
 *
 * By default the mutation settles after the refetch, for a caller that reads the fresh data next (the
 * work type creator resolves to an id in the refetched catalog). With `settleOnResponse` it settles on
 * the server's answer and the lists refresh behind it, so a slow refetch never holds a modal open.
 *
 * With `refusedInline` a refusal raises no toast: the caller shows it where it was refused (Add back, under
 * its row), so it shows once (walk 15's C12).
 */
export function financeMutationOptions<TResult>(effects: MutationEffects, success?: (result: TResult) => string, settleOnResponse = false, refusedInline = false) {
  return {
    retry: 0,
    onSuccess: (result: TResult): Promise<void> | undefined => {
      if (success) effects.toast({ message: success(result), type: "success" });
      const refetch = effects.refetch();
      return settleOnResponse ? undefined : refetch;
    },
    // A refusal means the cache was stale (another session changed it): refetch, so the action it offered goes. The
    // error settles at once, never waiting for the lists.
    onError: (error: Error): void => {
      if (!refusedInline) effects.toast({ message: error.message || "Finance request failed", type: "error" });
      void effects.refetch();
    },
  };
}

/** A ref the submit handler owns, read and set in the click itself. */
export interface SubmitLatch {
  current: boolean;
}

/**
 * Sends one request at a time from a modal's submit. The button disables only once React renders the pending
 * state, so a fast double click would send twice (the server's request key records one, but the second POST
 * still goes out). The latch is set in the first click; `submit` calls `release` once the request settles, so
 * a refused one can be corrected and sent again.
 */
export function submitOnce(latch: SubmitLatch, submit: (release: () => void) => void): void {
  if (latch.current) return;
  latch.current = true;
  submit(() => {
    latch.current = false;
  });
}

const stayOpen = (): void => undefined;

/** A modal's close while its request is in flight: Escape, the backdrop or the close button would drop the hand-over. */
export const closeUnlessPending = (pending: boolean, onClose: () => void): (() => void) => (pending ? stayOpen : onClose);

/** A field's error, once the field was touched: a form never opens in an error state (its primary stays disabled meanwhile). */
export const shownError = (error: string | undefined, touched: boolean): string | undefined => (touched ? error : undefined);
